import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { logActivity, startSession, endSession, pingSession } from '../services/logActivity';

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'admin' | 'engineer' | 'user'
  const [userInitials, setUserInitials] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [loading, setLoading] = useState(true);

  async function register(email, password, initials = '') {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await sendEmailVerification(result.user);
    await setDoc(doc(db, 'users', result.user.uid), {
      email,
      initials,
      role: 'user',
      createdAt: new Date().toISOString(),
      createdViaApp: true,
    });
    await logActivity(result.user.uid, email, 'login');
    return result;
  }

  async function resendVerificationEmail() {
    if (auth.currentUser) {
      await sendEmailVerification(auth.currentUser);
    }
  }

  async function login(email, password) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    await logActivity(result.user.uid, result.user.email, 'login');
    return result;
  }

  async function loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    // Создаём документ пользователя если его ещё нет
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) {
      const displayName = user.displayName || '';
      // Формируем инициалы из displayName Google
      const parts = displayName.trim().split(/\s+/);
      const initials = parts.length >= 2
        ? `${parts[0]} ${parts.slice(1).map(p => p[0] + '.').join('')}`
        : displayName;
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        initials,
        role: 'user',
        createdAt: new Date().toISOString(),
        createdViaApp: true,
        provider: 'google',
      });
    }
    await logActivity(user.uid, user.email, 'login');
    return result;
  }

  async function logout() {
    if (auth.currentUser) {
      await logActivity(auth.currentUser.uid, auth.currentUser.email, 'logout');
      // Закрываем сессию при явном выходе
      if (window.__sessionId) {
        await endSession(window.__sessionId, window.__sessionStartMs);
        window.__sessionId = null;
        window.__sessionStartMs = null;
      }
    }
    window.__workSessionStarted = false;
    return signOut(auth);
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  async function updateInitials(newInitials) {
    if (!auth.currentUser) return;
    await updateDoc(doc(db, 'users', auth.currentUser.uid), { initials: newInitials });
    setUserInitials(newInitials);
  }

  async function changePassword(currentPassword, newPassword) {
    const user = auth.currentUser;
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  }

  async function fetchUserData(uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch {
      return null;
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        const data = await fetchUserData(user.uid);
        setUserRole(data?.role || 'user');
        setUserInitials(data?.initials || '');

        // Обновляем lastSeen при входе
        try {
          await updateDoc(doc(db, 'users', user.uid), { lastSeen: serverTimestamp() });
        } catch {}

        if (window.__lastSeenInterval) {
          clearInterval(window.__lastSeenInterval);
          window.__lastSeenInterval = null;
        }

        // Новый визит (открыл приложение) — рабочая сессия + «Начал работу» в журнале
        if (!window.__workSessionStarted) {
          window.__workSessionStarted = true;
          startSession({ uid: user.uid, email: user.email, isGuest: false }).then((sessionId) => {
            if (sessionId) {
              window.__sessionId = sessionId;
              window.__sessionStartMs = Date.now();
            }
          });
        }

        const interval = setInterval(async () => {
          try {
            await updateDoc(doc(db, 'users', user.uid), { lastSeen: serverTimestamp() });
          } catch {}
          // Пингуем сессию — обновляем lastPing + durationSec каждую минуту
          if (window.__sessionId && window.__sessionStartMs) {
            pingSession(window.__sessionId, window.__sessionStartMs);
          }
        }, 60000); // каждую минуту
        // Сохраняем interval id чтобы очистить при выходе
        window.__lastSeenInterval = interval;
        // Показываем экран подтверждения только если аккаунт создан через сайт и email не подтверждён
        const registeredViaApp = data?.createdViaApp === true;
        setNeedsVerification(registeredViaApp && !user.emailVerified);
      } else {
        setUserRole(null);
        setUserInitials('');
        setNeedsVerification(false);
        if (window.__lastSeenInterval) {
          clearInterval(window.__lastSeenInterval);
          window.__lastSeenInterval = null;
        }
        if (window.__sessionId && window.__sessionStartMs) {
          endSession(window.__sessionId, window.__sessionStartMs);
          window.__sessionId = null;
          window.__sessionStartMs = null;
        }
        window.__workSessionStarted = false;
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // isAdmin — true для 'admin' и 'engineer' (инженеры имеют все те же права)
  const isAdmin = userRole === 'admin' || userRole === 'engineer';
  // isSuperAdmin — только для роли 'admin' (эксклюзивные функции)
  const isSuperAdmin = userRole === 'admin';
  const isEngineer = userRole === 'engineer';

  const value = {
    currentUser,
    userRole,
    isAdmin,
    isSuperAdmin,
    isEngineer,
    userInitials,
    needsVerification,
    register,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    resendVerificationEmail,
    updateInitials,
    changePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
